import { useMemo } from 'react';
import styled from 'styled-components';

import { setCaptionsHidden } from '@src/services/karaoke/captionsVisibility';

import { useKaraokeStore, useVideoCreatorStore } from '../../stores-v2';
import {
  GenericPresetTemplates,
  KaraokeCard,
  KaraokePresetTemplates,
} from '../../types.ts/general';
import PresetPreview from './PresetPreview';

import type { AspectRatio } from '../../types.ts/video';
import type { Preset } from '../textProcessor/TextBrandingService';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';

export default function GlobalKaraokePresetSelect({
  produceKaraokeText,
  config,
}: {
  produceKaraokeText: () => Promise<void>;
  config: KaraokeConfig;
}) {
  const videoCreator = useVideoCreatorStore();
  const karaokeStore = useKaraokeStore();
  const { globalKaraokePresets } = videoCreator.textBrandingService;
  const aspectRatio = videoCreator.getCurrentAspectRatio();
  const hasKaraoke =
    !!videoCreator.rootStore.editorStore?.editor?.getKaraokeLines()?.[0]?.id;
  // PLAT-11877 — Off from the player's Captions picker: track kept, no
  // template on the video, so no card is selected (as the workspace grid).
  const captionsOn = hasKaraoke && !karaokeStore?.captionsHidden;

  const presets = useMemo(
    preparePresetsToDisplay(globalKaraokePresets, aspectRatio),
    [globalKaraokePresets, aspectRatio],
  );

  const onSelectPreset = async (template: Preset) => {
    await produceKaraokeText();
    await videoCreator.textBrandingService.applyTemplate(
      template,
      'karaoke',
      null,
      undefined,
      false,
    );
    videoCreator.setActiveElements();
    // PLAT-11877 — a card pick while the captions are Off (hidden, from the
    // player's Captions picker) is a request to see them: the template goes
    // on, so the captions come back. Cleared after the produce and apply,
    // which both keep whatever visibility the config carried.
    const editor = videoCreator.rootStore.editorStore?.editor;
    if (
      editor &&
      setCaptionsHidden(editor, false, { createUndoRedoEntry: false })
    ) {
      karaokeStore.syncFromEditor(editor);
      videoCreator.rootStore.videoSourceStore?.refreshSource();
    }
    // Persist the applied caption template so it survives navigating
    // back to Content Studio. Without this the selection is only in
    // memory and the next reload reverts to the previous track.
    await videoCreator.saveStoryAndVideo();
  };

  const handleSettingsClick = () => {
    videoCreator.karaokeSubMenu = KaraokeCard.styles;
  };

  return (
    <Section data-testid="caption-presets-selector">
      <PresetsContainer>
        {presets.map((t, i) => {
          const isSelected = captionsOn && t.id === config.parent_template_id;
          return (
            <PresetPreview
              key={`${t.id}-${t.aspectRatio}-${i}`}
              data-testid={`caption-preset-${t.id}`}
              preset={t}
              onSelectPreset={onSelectPreset}
              selected={isSelected}
              onSettingsClick={isSelected ? handleSettingsClick : undefined}
            />
          );
        })}
      </PresetsContainer>
    </Section>
  );
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-sm);
  flex-shrink: 0;
  align-self: stretch;
`;

const PresetsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--spacing-md);
  align-self: stretch;
`;

// Presets we want to surface first (explicit priority order)
const KNOWN_PRESET_ORDER: string[] = [
  GenericPresetTemplates.default,
  KaraokePresetTemplates.classic,
  KaraokePresetTemplates.typewriter,
  KaraokePresetTemplates.edge,
];
const orderMap = new Map(KNOWN_PRESET_ORDER.map((id, idx) => [id, idx]));

export function preparePresetsToDisplay(
  globalKaraokePresets: Preset[],
  aspectRatio: AspectRatio,
): () => Preset[] {
  return () => {
    return globalKaraokePresets
      .filter(
        p =>
          p.aspectRatio === aspectRatio &&
          p.id !== GenericPresetTemplates.custom,
      )
      .sort((a, b) => {
        const aVal = orderMap.get(a.id);
        const bVal = orderMap.get(b.id);
        const aIdx = typeof aVal === 'number' ? aVal : Infinity;
        const bIdx = typeof bVal === 'number' ? bVal : Infinity;
        if (aIdx !== bIdx) return aIdx - bIdx;
        const aT =
          typeof a.title === 'string' ? a.title : String(a.title ?? '');
        const bT =
          typeof b.title === 'string' ? b.title : String(b.title ?? '');
        return aT.localeCompare(bT);
      });
  };
}
